import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { CopyIcon } from '@/shared/ui/icons'
import { SUPPORT_URL } from '@/shared/lib/contacts'
import { useUi } from '@/stores/RootStore'
import styles from './Appeal.module.css'
import { useMeta } from '@/shared/lib/hooks/useMeta'

/**
 * Апелляция по операции.
 *
 * Ручки в API нет — обращения по спорным операциям бекенд не принимает.
 * Поэтому экран НЕ делает вид, что заявка ушла: он собирает и проверяет
 * два идентификатора, складывает их в готовое сообщение и отдаёт
 * человеку, чтобы тот отправил его в поддержку.
 *
 * Кнопка «Отправить», после которой ничего не происходит, здесь была бы
 * худшим из вариантов: человек с потерянным платежом уходит ждать
 * ответа, которого никто не напишет, — и приходит через неделю уже
 * без денег и без терпения.
 */
export const AppealPage = observer(function AppealPage() {
  useMeta('Апелляция')
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const ui = useUi()

  // Из истории операций сюда можно прийти со ссылкой, где номер
  // уже подставлен: перепечатывать UUID руками — верный способ
  // ошибиться в одном символе и получить «операция не найдена».
  const [id, setId] = useState(params.get('tx') ?? '')
  const [hash, setHash] = useState(params.get('txid') ?? '')
  const [errors, setErrors] = useState<{ id?: string; hash?: string }>({})
  const [ready, setReady] = useState(false)

  const message = [
    'Апелляция по операции CryptoChangeK',
    `ID операции: ${id.trim()}`,
    `Hash транзакции: ${hash.trim() || '—'}`,
  ].join('\n')

  const check = () => {
    const next: { id?: string; hash?: string } = {}
    if (id.trim() === '') next.id = 'Укажите ID операции'
    // Hash необязателен: если перевод не дошёл до сети, его просто нет,
    // и требовать несуществующее значит закрыть обращение тем,
    // у кого проблема как раз в этом.
    if (hash.trim() !== '' && /\s/.test(hash.trim())) {
      next.hash = 'В hash не должно быть пробелов'
    }
    setErrors(next)
    if (Object.keys(next).length === 0) setReady(true)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message)
      ui.success('Обращение скопировано')
    } catch {
      ui.failure('Не удалось скопировать')
    }
  }

  return (
    <div className={styles.page}>
      {/* Карточка 608 по центру страницы, заголовок ВНУТРИ неё —
          так в макете (Payment Card 608x380, отступы 32, зазор 32). */}
      <section className={styles.card}>
        <h1 className={styles.title}>Апелляция по заявке</h1>

        <div className={styles.fields}>
          <Field label="ID транзакции" placeholder="Укажите ID"
                 value={id} onChange={(e) => { setId(e.target.value); setReady(false) }}
                 error={errors.id ?? null} />

          <Field label="Hash транзакции" placeholder="Укажите hash"
                 value={hash} onChange={(e) => { setHash(e.target.value.trim()); setReady(false) }}
                 error={errors.hash ?? null} />
        </div>

        {ready ? (
          <>
            <pre className={styles.message}>{message}</pre>
            <div className={styles.actions}>
              <Button variant="secondary" onClick={() => void copy()}>
                <CopyIcon size={24} /> Скопировать
              </Button>
              <a className={styles.send} href={SUPPORT_URL}
                 target="_blank" rel="noopener noreferrer"
                 onClick={() => void copy()}>
                Открыть поддержку
              </a>
            </div>
          </>
        ) : (
          <Button variant="primary" block onClick={check}>Отправить</Button>
        )}
      </section>

      <button type="button" className={styles.back} onClick={() => navigate('/app/history')}>
        ← К истории
      </button>
    </div>
  )
})
